import React, { useCallback, useEffect, useRef, useState } from "react";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import EditIcon from "@mui/icons-material/Edit";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Paper,
  TableContainer,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";

import GenerarUnidadesService from "../../../../../../services/GenerarUnidadesService";
import UnidadService from "../../../../../../services/UnidadService";
import ModeracionService from "../../../../../../services/ModeracionService";

const STORAGE_STEP2 = "sac_step2_data";
const STORAGE_STEP5 = "sac_step5_data";
const step5Styles = {
  width: "100%",
  boxSizing: "border-box",
  "& .step5-title": {
    color: "#8f8f8f",
    fontWeight: 500,
    letterSpacing: "0.2px",
    textAlign: "left",
    marginBottom: "18px",
  },
  "& .step5-alert.MuiAlert-root": {
    borderRadius: "10px",
    marginBottom: "14px",
  },
  "& .step5-table.MuiPaper-root": {
    border: "1px solid #d6d6d6",
    borderRadius: "6px",
    boxShadow: "none",
    overflow: "auto",
  },
  "& .step5-table-inner": {
    minWidth: "980px",
  },
  "& .step5-thead .step5-th.MuiTableCell-root, & .step5-table .MuiTableCell-head": {
    background: "#9f9f9f",
    color: "#ffffff",
    fontWeight: 700,
    borderBottom: "1px solid #d6d6d6",
    padding: "10px 12px",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
  },
  "& .step5-th-move": { width: "86px" },
  "& .step5-th-nro": { width: "110px" },
  "& .step5-th-actions": { width: "240px", textAlign: "center" },
  "& .step5-td.MuiTableCell-root, & .step5-table .MuiTableCell-body": {
    color: "#6f6f6f",
    borderBottom: "1px solid #ececec",
    padding: "14px 12px",
    verticalAlign: "middle",
    fontSize: "13px",
    lineHeight: 1.35,
  },
  "& .step5-td-move": {
    width: "86px",
    textAlign: "center",
    whiteSpace: "nowrap",
  },
  "& .step5-td-nro": {
    width: "110px",
    textAlign: "center",
  },
  "& .step5-td-text": {
    whiteSpace: "normal",
    wordBreak: "break-word",
  },
  "& .step5-td-desc": {
    maxWidth: "560px",
  },
  "& .step5-td-actions": {
    width: "240px",
    textAlign: "center",
    verticalAlign: "middle",
  },
  "& .step5-tr:hover .step5-td.MuiTableCell-root": {
    background: "#fafafa",
  },
  "& .step5-move-btn.MuiIconButton-root": {
    color: "#9c9c9c",
    padding: "6px",
    borderRadius: "10px",
  },
  "& .step5-move-btn.MuiIconButton-root:hover": {
    color: "#00c7db",
    background: "rgba(0, 199, 219, 0.10)",
  },
  "& .step5-move-btn.Mui-disabled": {
    opacity: 0.4,
  },
  "& .step5-actions-stack": {
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
    width: "100%",
  },
  "& .step5-action-btn.MuiButton-root": {
    padding: 0,
    minWidth: "unset",
    background: "transparent",
    color: "#00c7db",
    fontWeight: 800,
    letterSpacing: "0.6px",
    textTransform: "uppercase",
    lineHeight: 1.2,
  },
  "& .step5-action-btn.MuiButton-root:hover": {
    background: "transparent",
    color: "#00a9b9",
    textDecoration: "underline",
  },
  "& .step5-action-btn .MuiButton-startIcon": {
    marginRight: "10px",
  },
  "& .step5-footer": {
    marginTop: "22px",
    display: "flex",
    gap: "16px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  "& .step5-btn.MuiButton-root": {
    borderRadius: "999px",
    height: "44px",
    minWidth: "180px",
    padding: "0 22px",
    fontWeight: 800,
    letterSpacing: "0.6px",
    textTransform: "uppercase",
    boxShadow: "none",
  },
  "& .step5-btn-back.MuiButton-outlined": {
    border: "2px solid #00c7db",
    color: "#00c7db",
    background: "transparent",
  },
  "& .step5-btn-back.MuiButton-outlined:hover": {
    borderColor: "#00a9b9",
    color: "#00a9b9",
    background: "rgba(0, 199, 219, 0.08)",
  },
  "& .step5-btn-primary.MuiButton-contained, & .step5-btn-secondary.MuiButton-contained, & .step5-btn-save.MuiButton-contained": {
    background: "#00c7db",
    color: "#ffffff",
  },
  "& .step5-btn-primary.MuiButton-contained:hover, & .step5-btn-secondary.MuiButton-contained:hover, & .step5-btn-save.MuiButton-contained:hover": {
    background: "#00a9b9",
  },
  "& .step5-btn.Mui-disabled": {
    opacity: 1,
    background: "#e0e0e0",
    color: "#b6b6b6",
  },
  "@media (max-width:900px)": {
    "& .step5-table-inner": {
      minWidth: "860px",
    },
    "& .step5-th-actions, & .step5-td-actions": {
      width: "220px",
    },
  },
  "@media (max-width:600px)": {
    "& .step5-title": {
      fontSize: "26px",
    },
    "& .step5-table-inner": {
      minWidth: "760px",
    },
    "& .step5-footer": {
      flexDirection: "column",
      alignItems: "stretch",
    },
    "& .step5-btn.MuiButton-root": {
      width: "100%",
      minWidth: "100%",
    },
  },
};

const UnitSituationStep = ({ onNext, onBack, setUnidadesSeleccionadas, userRole, goContextStep }) => {
  const hasAutoGeneratedRef = useRef(false);
  const [unidades, setUnidades] = useState([]);
  const [situacionGenerada, setSituacionGenerada] = useState(false);
  const [autoGenerationFailed, setAutoGenerationFailed] = useState(false);

  const [loadingGeneral, setLoadingGeneral] = useState(false);
  const [loadingFila, setLoadingFila] = useState(null);
  const [loadingGuardar, setLoadingGuardar] = useState(false);

  const [guardadoExitoso, setGuardadoExitoso] = useState(false);
  const [guardadoEnBackend, setGuardadoEnBackend] = useState(false);
  const [hayCambios, setHayCambios] = useState(false);

  const [openEdit, setOpenEdit] = useState(false);
  const [filaEditando, setFilaEditando] = useState(null);
  const [filaAnimada, setFilaAnimada] = useState(null);

  const [error409, setError409] = useState(false);

  const [openViewMore, setOpenViewMore] = useState(false);
  const [filaViewMore, setFilaViewMore] = useState(null);
  const [openUnsavedWarning, setOpenUnsavedWarning] = useState(false);

  const normArr = (arr) => (Array.isArray(arr) ? arr.map(String) : []);

  /* ===========================
     Snackbar (mensaje bonito)
  =========================== */
  const [snack, setSnack] = useState({
    open: false,
    severity: "error", // "success" | "error" | "warning" | "info"
    message: "",
  });

  const showSnack = useCallback((message, severity = "error") => {
    setSnack({ open: true, severity, message });
  }, []);

  const closeSnack = (_, reason) => {
    if (reason === "clickaway") return;
    setSnack((p) => ({ ...p, open: false }));
  };

  const getModeracionMessage = useCallback((err) => {
    const detail = err?.response?.data?.detail;
    return detail?.message || "Contenido no permitido (malas palabras o +18).";
  }, []);

  /**
   * Validar moderación para Unidad + Situación
   */
  const validarModeracion = useCallback(async (unidadNombre, situacionTexto, severity = "error") => {
    try {
      await ModeracionService.validarContenido({
        tema: unidadNombre || "",
        objetivo: situacionTexto || "",
        sub_area_tematica: "SAC",
      });
      return true;
    } catch (err) {
      showSnack(getModeracionMessage(err), severity);
      return false;
    }
  }, [getModeracionMessage, showSnack]);

  // =====================================================
  // CARGA INICIAL
  // =====================================================
  useEffect(() => {
    let step2 = null;
    let step5 = null;

    try {
      step2 = JSON.parse(localStorage.getItem(STORAGE_STEP2));
      step5 = JSON.parse(localStorage.getItem(STORAGE_STEP5));
    } catch {
      step2 = null;
      step5 = null;
    }

    if (!step2) return;

    const { seleccionados = [], unidadesBackend = [] } = step2;

    const base = seleccionados
      .map((id) => unidadesBackend.find((u) => String(u.id) === String(id)))
      .filter(Boolean)
      .map((u, i) => ({
        id: u.id,
        nombre: u.nombre,
        nroUnidad: i + 1,
        prioridad: "",
      }));

    const mismos =
      step5?.seleccionadosIds &&
      JSON.stringify(normArr(step5.seleccionadosIds)) === JSON.stringify(normArr(seleccionados));

    if (mismos && step5?.tablaConsolidada?.length) {
      const yaGuardadas = Boolean(step5?.guardadoEnBackend);
      setUnidades(step5.tablaConsolidada);
      setSituacionGenerada(step5.situacionGenerada);
      setGuardadoEnBackend(yaGuardadas);
      setHayCambios(!yaGuardadas);
    } else {
      setUnidades(base);
      setSituacionGenerada(false);
      setGuardadoEnBackend(false);
      setHayCambios(true);
    }
  }, []);

  // =====================================================
  // LOCALSTORAGE
  // =====================================================
  const guardarEstado = useCallback((data, generado, guardado = false) => {
    let step2 = null;
    try {
      step2 = JSON.parse(localStorage.getItem(STORAGE_STEP2));
    } catch {
      step2 = null;
    }

    localStorage.setItem(
      STORAGE_STEP5,
      JSON.stringify({
        tablaConsolidada: data,
        situacionGenerada: generado,
        seleccionadosIds: step2?.seleccionados ?? [],
        guardadoEnBackend: guardado,
      })
    );
  }, []);

  // =====================================================
  // GENERAR TODAS (+ MODERACIÓN)
  // =====================================================
  const handleGenerarSituacion = useCallback(async ({ showSuccessMessage = true } = {}) => {
    setLoadingGeneral(true);
    setGuardadoExitoso(false);
    setAutoGenerationFailed(false);

    try {
      const titulos = unidades.map((u) => u.nombre);
      const res = await GenerarUnidadesService.generarSituacionSignificativa(titulos);

      const actualizadas = unidades.map((u, i) => ({
        ...u,
        prioridad: res[i]?.situacion || "",
      }));

      for (const u of actualizadas) {
        const ok = await validarModeracion(u.nombre, u.prioridad, "warning");
        if (!ok) {
          // no pisar estado/localStorage con contenido bloqueado
          return;
        }
      }

      setUnidades(actualizadas);
      setSituacionGenerada(true);
      setGuardadoEnBackend(false);
      setHayCambios(true);
      guardarEstado(actualizadas, true, false);

      if (showSuccessMessage) {
        showSnack("Situaciones generadas correctamente.", "success");
      }
    } catch (err) {
      console.error("Error generando situación:", err);
      setAutoGenerationFailed(true);
      showSnack("Error generando situaciones.", "error");
    } finally {
      setLoadingGeneral(false);
    }
  }, [guardarEstado, showSnack, unidades, validarModeracion]);

  useEffect(() => {
    if (!unidades.length || situacionGenerada || hasAutoGeneratedRef.current) return;

    hasAutoGeneratedRef.current = true;
    handleGenerarSituacion({ showSuccessMessage: false });
  }, [handleGenerarSituacion, situacionGenerada, unidades.length]);

  // =====================================================
  // REGENERAR FILA (+ MODERACIÓN)
  // =====================================================
  const regenerarFila = async (id) => {
    setLoadingFila(id);
    setGuardadoExitoso(false);

    try {
      const fila = unidades.find((u) => u.id === id);
      const res = await GenerarUnidadesService.regenerarSituacion(fila.nombre);

      const nuevaSituacion = res?.situacion || "";

      // ✅ Moderación de la fila
      const ok = await validarModeracion(fila.nombre, nuevaSituacion, "error");
      if (!ok) return;

      const actualizado = unidades.map((u) => (u.id === id ? { ...u, prioridad: nuevaSituacion } : u));

      setUnidades(actualizado);
      setGuardadoEnBackend(false);
      setHayCambios(true);
      guardarEstado(actualizado, true, false);

      showSnack("Situación regenerada correctamente.", "success");
    } catch (err) {
      console.error("Error regenerando fila:", err);
      showSnack("Error regenerando la situación.", "error");
    } finally {
      setLoadingFila(null);
    }
  };

  // =====================================================
  // GUARDAR DEFINITIVO
  // =====================================================
  const handleGuardar = async () => {
    setError409(false);
    setLoadingGuardar(true);
    setGuardadoExitoso(false);
    try {
      await UnidadService.guardarUnidades({ unidades });
      setUnidadesSeleccionadas(unidades);
      guardarEstado(unidades, true, true);
      setGuardadoExitoso(true);
      setGuardadoEnBackend(true);
      setHayCambios(false);

      showSnack("Unidades guardadas correctamente.", "success");
    } catch (err) {
      console.error("Error guardando unidades:", err);
      if (err?.response?.status === 409) setError409(true);
      else showSnack("Error al guardar unidades.", "error");
    } finally {
      setLoadingGuardar(false);
    }
  };

  // =====================================================
  // REORDENAR
  // =====================================================
  const moverFila = (index, direccion) => {
    setUnidades((prev) => {
      const copia = [...prev];

      let idSube = null;
      let idBaja = null;

      if (direccion === "up" && index > 0) {
        idSube = copia[index].id;
        idBaja = copia[index - 1].id;
        [copia[index - 1], copia[index]] = [copia[index], copia[index - 1]];
      }

      if (direccion === "down" && index < copia.length - 1) {
        idSube = copia[index + 1].id;
        idBaja = copia[index].id;
        [copia[index + 1], copia[index]] = [copia[index], copia[index + 1]];
      }

      const renumerado = copia.map((u, i) => ({
        ...u,
        nroUnidad: i + 1,
      }));

      setFilaAnimada({ sube: idSube, baja: idBaja });
      setGuardadoEnBackend(false);
      setHayCambios(true);
      guardarEstado(renumerado, true, false);

      return renumerado;
    });

    setTimeout(() => setFilaAnimada(null), 400);
  };

  // =====================================================
  // EDITAR (+ MODERACIÓN) + FIX LABEL
  // =====================================================
  const abrirEditar = (fila) => {
    setFilaEditando({ ...fila });
    setOpenEdit(true);
  };

  const guardarEdicion = async () => {
    if (!filaEditando) return;

    const nombre = (filaEditando.nombre || "").trim();
    const situacion = (filaEditando.prioridad || "").trim();

    const ok = await validarModeracion(nombre, situacion, "error");
    if (!ok) return;

    const actualizado = unidades.map((u) => (u.id === filaEditando.id ? { ...filaEditando, nombre, prioridad: situacion } : u));
    setUnidades(actualizado);
    setGuardadoEnBackend(false);
    setHayCambios(true);
    guardarEstado(actualizado, true, false);
    setOpenEdit(false);

    showSnack("Cambios guardados correctamente.", "success");
  };

  const handleIrAGenerarSesiones = () => {
    const hayContenidoGenerado = situacionGenerada && unidades.length > 0 && unidades.every((u) => (u.prioridad || "").trim());

    if (!hayContenidoGenerado) {
      showSnack("Primero asegúrate de tener situaciones significativas generadas para todas las unidades.", "warning");
      return;
    }

    if (!guardadoEnBackend || hayCambios) {
      setOpenUnsavedWarning(true);
      return;
    }

    onNext();
  };

  // 🔥 NUEVA FUNCIÓN: Abrir "Ver Más"
  const handleAbrirVerMas = (fila) => {
    setFilaViewMore(fila);
    setOpenViewMore(true);
  };

  // =====================================================
  // RENDER
  // =====================================================
  return (
    <Box className="step5-root" sx={step5Styles}>
      <Typography variant="h4" className="step5-title" gutterBottom>
        Situación Significativa para cada Unidad Didáctica
      </Typography>

      {guardadoExitoso && (
        <Alert severity="success" className="step5-alert">
          Unidades guardadas correctamente
        </Alert>
      )}

      {/* TABLA */}
      <TableContainer component={Paper} className="step5-table">
        <Table className="step5-table-inner">
          <TableHead className="step5-thead">
            <TableRow>
              <TableCell className="step5-th step5-th-move" />
              <TableCell className="step5-th step5-th-nro">Nro</TableCell>
              <TableCell className="step5-th">Unidad</TableCell>

              {situacionGenerada && (
                <>
                  <TableCell className="step5-th">Situación Significativa</TableCell>
                  <TableCell className="step5-th step5-th-actions">Acciones</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {unidades.map((u, index) => (
              <TableRow
                key={u.id}
                className="step5-tr"
                sx={{
                  transition: "background-color 0.4s ease",
                  // 🔥 FIX: Fuerza a que todo el contenido de las celdas (td) se centre verticalmente
                  "& td": { verticalAlign: "middle" },
                  backgroundColor:
                    filaAnimada?.sube === u.id
                      ? "rgba(76, 175, 80, 0.18)"
                      : filaAnimada?.baja === u.id
                        ? "rgba(244, 67, 54, 0.18)"
                        : "transparent",
                }}
              >
                <TableCell className="step5-td step5-td-move">
                  {/* Contenedor Flex para alinear bien las flechas si están en columna o fila */}
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    <IconButton
                      size="small"
                      className="step5-move-btn"
                      disabled={index === 0}
                      onClick={() => moverFila(index, "up")}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>

                    <IconButton
                      size="small"
                      className="step5-move-btn"
                      disabled={index === unidades.length - 1}
                      onClick={() => moverFila(index, "down")}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>

                <TableCell className="step5-td step5-td-nro">{u.nroUnidad}</TableCell>
                <TableCell className="step5-td step5-td-text">
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "13px",
                      lineHeight: 1.45,
                      color: "text.primary",
                    }}
                  >
                    {u.nombre}
                  </Typography>
                </TableCell>

                {situacionGenerada && (
                  <>
                    {/* CELDA COMPACTA CON "VER MÁS" */}
                    <TableCell className="step5-td step5-td-text step5-td-desc">
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "13px",
                            color: "text.secondary",
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: 3,
                            overflow: "hidden",
                            lineHeight: 1.45,
                            minHeight: "calc(1.45em * 3)",
                          }}
                        >
                          {u.prioridad}
                        </Typography>
                        {(u.prioridad || "").length > 180 && (
                          <Typography
                            component="span"
                            variant="body2"
                            onClick={() => handleAbrirVerMas(u)}
                            sx={{
                              color: "#1976d2",
                              cursor: "pointer",
                              fontWeight: 600,
                              textTransform: "uppercase",
                              display: "inline-block",
                              ml: 0.5,
                            }}
                          >
                            VER MÁS
                          </Typography>
                        )}
                      </Box>
                    </TableCell>

                    <TableCell className="step5-td step5-td-actions">
                      {/* Acciones en vertical centraditas */}
                      <Box className="step5-actions-stack" sx={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 1 }}>
                        <Button
                          variant="text"
                          className="step5-action-btn"
                          startIcon={
                            loadingFila === u.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <AutorenewIcon />
                            )
                          }
                          disabled={loadingFila === u.id}
                          onClick={() => regenerarFila(u.id)}
                        >
                          Regenerar situación
                        </Button>

                        <Button
                          variant="text"
                          className="step5-action-btn"
                          startIcon={<EditIcon />}
                          onClick={() => abrirEditar(u)}
                        >
                          Editar
                        </Button>
                      </Box>
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* BOTONES */}
      <Box
        className="step5-footer"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          mt: 3 // Margen superior opcional si necesitas que respire más respecto a la tabla
        }}
      >
        {/* Grupo Izquierda */}
        <Box sx={{ display: "flex", gap: 2 }}>
          {typeof goContextStep === "function" && (
            <Button
              variant="outlined"
              onClick={goContextStep}
              className="step5-btn step5-btn-back"
            >
              Volver al contexto
            </Button>
          )}

          <Button variant="outlined" onClick={onBack} className="step5-btn step5-btn-back">
            Atrás
          </Button>

          {!situacionGenerada && autoGenerationFailed && (
            <Button
              variant="contained"
              onClick={() => handleGenerarSituacion()}
              disabled={loadingGeneral}
              className="step5-btn step5-btn-primary"
            >
              {loadingGeneral ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Reintentar generación"
              )}
            </Button>
          )}
        </Box>

        {/* Grupo Derecha */}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {situacionGenerada && (
            <Button
              variant="contained"
              onClick={handleGuardar}
              disabled={loadingGuardar || !hayCambios || error409}
              className="step5-btn step5-btn-save"
            >
              {loadingGuardar ? <CircularProgress size={20} color="inherit" /> : "Guardar Unidades"}
            </Button>
          )}

          {(situacionGenerada && (userRole === "ADMIN" || userRole === "DIRECTOR")) && (
            <Button
              variant="outlined"
              onClick={handleIrAGenerarSesiones}
              className="step5-btn step5-btn-back"
            >
              Ir a Generar Sesiones
            </Button>
          )}
        </Box>
      </Box>

      {/* MODAL EDITAR */}
      <Dialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: "16px", // Esquinas redondeadas modernas
            padding: "8px", // Respiro interno general
            boxShadow: "0px 16px 40px rgba(0,0,0,0.08)", // Sombra elegante y suave
          }
        }}
      >
        <DialogTitle sx={{
          fontWeight: 800,
          fontSize: "1.4rem",
          color: "#2d3748",
          pb: 1
        }}>
          Editar Unidad {filaEditando?.nroUnidad}
        </DialogTitle>

        {/* pt: "12px !important" evita que el primer input se pegue al título */}
        <DialogContent sx={{ overflow: "visible", pt: "12px !important" }}>
          <TextField
            fullWidth
            label="Título de la Unidad"
            value={filaEditando?.nombre || ""}
            onChange={(e) => setFilaEditando({ ...filaEditando, nombre: e.target.value })}
            sx={{
              mb: 3, // Separación con el siguiente input
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: "#f8fafc", // Fondo muy sutil para destacar el input
              }
            }}
          />

          <TextField
            fullWidth
            multiline
            minRows={5}
            label="Situación Significativa"
            value={filaEditando?.prioridad || ""}
            onChange={(e) => setFilaEditando({ ...filaEditando, prioridad: e.target.value })}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: "#f8fafc",
                lineHeight: 1.6,
                textAlign: "justify"
              }
            }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            onClick={() => setOpenEdit(false)}
            sx={{
              color: "#64748b",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "8px",
              px: 3
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={guardarEdicion}
            sx={{
              bgcolor: "#00c7db", // Color accent de tu plataforma
              color: "#fff",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "8px",
              px: 4,
              boxShadow: "0px 6px 16px rgba(0, 199, 219, 0.25)",
              "&:hover": {
                bgcolor: "#00a9b9",
                boxShadow: "0px 8px 20px rgba(0, 199, 219, 0.35)",
              }
            }}
          >
            Guardar Cambios
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL VER MÁS*/}
      <Dialog
        open={openViewMore}
        onClose={() => setOpenViewMore(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: "16px", // Esquinas redondeadas
            padding: "8px", // Respiro interno general
            boxShadow: "0px 16px 40px rgba(0,0,0,0.08)", // Sombra elegante
          }
        }}
      >
        <DialogTitle sx={{
          fontWeight: 800,
          fontSize: "1.4rem",
          color: "#2d3748",
          pb: 1,
          pt: 2
        }}>
          Unidad {filaViewMore?.nroUnidad}: {filaViewMore?.nombre}
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            borderBottom: "none",
            borderColor: "#f0f4f8" // Línea divisoria superior mucho más suave
          }}
        >
          <Typography
            sx={{
              whiteSpace: "pre-wrap",
              color: "#475569", // Gris pizarra, excelente para lectura
              lineHeight: 1.7, // Más interlineado para que no canse la vista
              fontSize: "1rem",
              pt: 1,
              textAlign: "justify"
            }}
          >
            {filaViewMore?.prioridad}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ pr: 3, pb: 2, pt: 1 }}>
          <Button
            onClick={() => setOpenViewMore(false)}
            sx={{
              color: "#00c7db", // Color accent de tu plataforma en lugar del azul genérico
              fontWeight: 800,
              textTransform: "none", // Quita el "TODO MAYÚSCULAS" para verse más moderno
              fontSize: "1rem",
              borderRadius: "8px",
              px: 3,
              "&:hover": {
                bgcolor: "rgba(0, 199, 219, 0.08)" // Hover súper sutil
              }
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openUnsavedWarning}
        onClose={() => setOpenUnsavedWarning(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            padding: "8px",
            boxShadow: "0px 16px 40px rgba(0,0,0,0.08)",
          }
        }}
      >
        <DialogTitle sx={{
          fontWeight: 800,
          fontSize: "1.3rem",
          color: "#ed6c02",
          pb: 1
        }}>
          Continuar sin guardar
        </DialogTitle>

        <DialogContent sx={{ pt: "8px !important" }}>
          <Typography sx={{ color: "#475569", lineHeight: 1.6, mb: 2 }}>
            Hay cambios en las unidades o situaciones significativas que todavia no se han guardado.
          </Typography>

          <Alert severity="warning" sx={{ borderRadius: "10px", alignItems: "center" }}>
            Puedes continuar a la generacion de sesiones, pero esta planificacion no quedara guardada en el backend hasta que pulses <strong>Guardar Unidades</strong>.
          </Alert>
        </DialogContent>

        <DialogActions sx={{ pr: 3, pb: 2, pt: 1 }}>
          <Button
            onClick={() => setOpenUnsavedWarning(false)}
            sx={{
              color: "#64748b",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "8px",
              px: 3
            }}
          >
            Volver
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setOpenUnsavedWarning(false);
              onNext();
            }}
            sx={{
              bgcolor: "#ed6c02",
              color: "#fff",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "8px",
              px: 4,
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#d65f00",
                boxShadow: "none"
              }
            }}
          >
            Continuar sin guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL 409: LÍMITE DE AÑO ALCANZADO */}
      <Dialog
        open={error409}
        onClose={() => setError409(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            padding: "8px",
            boxShadow: "0px 16px 40px rgba(0,0,0,0.08)",
          }
        }}
      >
        <DialogTitle sx={{
          fontWeight: 800,
          fontSize: "1.3rem",
          color: "#d32f2f", // Rojo sutil para indicar un bloqueo
          pb: 1
        }}>
          No es posible guardar
        </DialogTitle>

        <DialogContent sx={{ pt: "8px !important" }}>
          <Typography sx={{ color: "#475569", lineHeight: 1.6, mb: 2 }}>
            El sistema detecta que ya existen unidades planificadas para este año académico.
          </Typography>

          {/* Cuadro de sugerencia destacado */}
          <Alert severity="warning" sx={{ borderRadius: "10px", alignItems: "center" }}>
            <strong>Sugerencia:</strong> Para poder guardar esta nueva planificación, primero debes ir al Buscador de sesiones y <strong>eliminar las sesiones</strong> que han sido generadas previamente en este mismo año.
          </Alert>
        </DialogContent>

        <DialogActions sx={{ pr: 3, pb: 2, pt: 1 }}>
          <Button
            variant="contained"
            onClick={() => setError409(false)}
            sx={{
              bgcolor: "#475569", // Un gris oscuro elegante, ya que no es una acción de éxito
              color: "#fff",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "8px",
              px: 4,
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#334155",
                boxShadow: "none"
              }
            }}
          >
            Entendido
          </Button>
        </DialogActions>
      </Dialog>

      {/* SNACKBAR */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={closeSnack}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={closeSnack} severity={snack.severity} variant="filled" sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UnitSituationStep;
